import * as THREE from 'three';
import type { InputController } from '../core/InputController';
import { createElephantVisual, type ElephantAssetDiagnostics } from './ElephantVisual';

export type PlayerTuning = {
  speed: number;
  dashMultiplier: number;
  acceleration: number;
};

export type ArenaBounds = {
  halfWidth: number;
  halfDepth: number;
};

export type PlayerVitals = {
  health: number;
  maxHealth: number;
  trunkWater: number;
  maxTrunkWater: number;
  heat: number;
  collapsed: boolean;
};

export class Player {
  readonly group = new THREE.Group();
  private readonly visual = createElephantVisual();
  readonly visualRoot = this.visual.root;
  readonly trunkTip = this.visual.trunkTip;
  readonly carrySocket = this.visual.carrySocket;
  readonly spraySocket = this.visual.spraySocket;
  readonly velocity = new THREE.Vector3();
  readonly radius = 1.15;
  readonly maxHealth = 100;
  readonly maxTrunkWater = 100;

  health = this.maxHealth;
  trunkWater = 0;
  heat = 0;
  collapsed = false;
  spraying = false;
  carrying = false;
  trunkLift = 0;
  trunkCurl = 0;
  trunkReach = 0;
  trunkYaw = 0;

  private readonly move = new THREE.Vector2();
  private readonly targetVelocity = new THREE.Vector3();
  private readonly movementForward = new THREE.Vector3(0, 0, -1);
  private readonly movementRight = new THREE.Vector3(1, 0, 0);
  private readonly desiredDirection = new THREE.Vector3();

  constructor() {
    this.group.name = 'elephant-player';
    this.group.add(this.visualRoot);
  }

  update(
    delta: number,
    elapsed: number,
    input: InputController,
    tuning: PlayerTuning,
    bounds: ArenaBounds,
    cameraForward?: THREE.Vector3,
    cameraRight?: THREE.Vector3,
    animationDelta = delta,
  ): void {
    input.readMovement(this.move);
    if (cameraForward) this.movementForward.copy(cameraForward).setY(0).normalize();
    if (cameraRight) this.movementRight.copy(cameraRight).setY(0).normalize();
    const sprint = input.isSprintHeld() ? tuning.dashMultiplier : 1;
    this.desiredDirection
      .copy(this.movementForward)
      .multiplyScalar(this.move.y)
      .addScaledVector(this.movementRight, this.move.x);
    if (this.desiredDirection.lengthSq() > 1) this.desiredDirection.normalize();
    this.targetVelocity.copy(this.desiredDirection).multiplyScalar(tuning.speed * sprint);
    if (this.collapsed) this.targetVelocity.set(0, 0, 0);

    this.velocity.lerp(this.targetVelocity, 1 - Math.exp(-tuning.acceleration * delta));
    this.group.position.addScaledVector(this.velocity, delta);
    this.group.position.x = THREE.MathUtils.clamp(
      this.group.position.x,
      -bounds.halfWidth + this.radius,
      bounds.halfWidth - this.radius,
    );
    this.group.position.z = THREE.MathUtils.clamp(
      this.group.position.z,
      -bounds.halfDepth + this.radius,
      bounds.halfDepth - this.radius,
    );

    if (this.velocity.lengthSq() > 0.03) {
      // The elephant model faces local -Z, whose world X component is -sin(yaw).
      const desiredYaw = Math.atan2(-this.velocity.x, -this.velocity.z);
      this.group.rotation.y = this.lerpAngle(
        this.group.rotation.y,
        desiredYaw,
        1 - Math.exp(-delta * 8.5),
      );
    }

    this.visual.update(animationDelta, elapsed, {
      speed: this.velocity.length(),
      spraying: this.spraying,
      carrying: this.carrying,
      collapsed: this.collapsed,
      trunkLift: this.trunkLift,
      trunkCurl: this.trunkCurl,
      trunkReach: this.trunkReach,
      trunkYaw: this.trunkYaw,
      healthRatio: this.health / this.maxHealth,
      waterRatio: this.trunkWater / this.maxTrunkWater,
    });
  }

  fillWater(amount: number): number {
    const previous = this.trunkWater;
    this.trunkWater = THREE.MathUtils.clamp(this.trunkWater + amount, 0, this.maxTrunkWater);
    return this.trunkWater - previous;
  }

  consumeWater(amount: number): number {
    const consumed = Math.min(this.trunkWater, Math.max(0, amount));
    this.trunkWater -= consumed;
    return consumed;
  }

  restoreHealth(amount: number): number {
    const previous = this.health;
    this.health = THREE.MathUtils.clamp(this.health + amount, 0, this.maxHealth);
    if (this.health > 0) this.collapsed = false;
    return this.health - previous;
  }

  applyDamage(amount: number): boolean {
    if (this.collapsed) return false;
    this.health = Math.max(0, this.health - Math.max(0, amount));
    if (this.health <= 0) {
      this.collapsed = true;
      this.velocity.set(0, 0, 0);
      return true;
    }
    return false;
  }

  setHeat(value: number): void {
    this.heat = THREE.MathUtils.clamp(value, 0, 1);
  }

  reset(position = new THREE.Vector3()): void {
    this.group.position.copy(position);
    this.group.rotation.set(0, 0, 0);
    this.visualRoot.position.set(0, 0, 0);
    this.visualRoot.rotation.set(0, 0, 0);
    this.velocity.set(0, 0, 0);
    this.health = this.maxHealth;
    this.trunkWater = 0;
    this.heat = 0;
    this.collapsed = false;
    this.spraying = false;
    this.carrying = false;
    this.trunkLift = 0;
    this.trunkCurl = 0;
    this.trunkReach = 0;
    this.trunkYaw = 0;
    this.visual.reset();
  }

  getVitals(): PlayerVitals {
    return {
      health: this.health,
      maxHealth: this.maxHealth,
      trunkWater: this.trunkWater,
      maxTrunkWater: this.maxTrunkWater,
      heat: this.heat,
      collapsed: this.collapsed,
    };
  }

  getVisualDiagnostics(): ElephantAssetDiagnostics {
    return this.visual.assetDiagnostics();
  }

  dispose(): void {
    this.visual.dispose();
  }

  private lerpAngle(from: number, to: number, alpha: number): number {
    const delta = THREE.MathUtils.euclideanModulo(to - from + Math.PI, Math.PI * 2) - Math.PI;
    return from + delta * alpha;
  }
}
